import { useEffect, useState } from "react";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "bootstrap-icons/font/bootstrap-icons.css";
import Input from "./campaign/wizard/Input";
import { auth } from "../utils/firebase";
import { useLogoutPrompt } from "../context/LogoutPromptContext";
import PageHeader, { PageShell } from "./shared/PageHeader";
import {
    DEFAULT_USER_PREFERENCES,
    getUserPreferences,
    saveUserPreferences,
} from "../utils/userPreferences";

const PLATFORM_OPTIONS = [
    { value: "instagram", label: "Instagram", icon: "bi-instagram", iconClass: "text-pink-600" },
    { value: "facebook", label: "Facebook", icon: "bi-facebook", iconClass: "text-blue-600" },
    { value: "linkedin", label: "LinkedIn", icon: "bi-linkedin", iconClass: "text-blue-700" },
    { value: "twitter", label: "X / Twitter", icon: "bi-twitter-x", iconClass: "text-slate-800" },
];

const LANGUAGE_OPTIONS = [
    { value: "english", label: "English", badge: "English" },
    { value: "hindi", label: "Hindi", badge: "हिंदी" },
    { value: "bengali", label: "Bengali", badge: "বাংলা" },
];

const Settings = () => {
    const { requestLogout } = useLogoutPrompt();
    const user = useSelector((state) => state.auth.user);
    const userId = user?.uid;

    const [preferences, setPreferences] = useState(DEFAULT_USER_PREFERENCES);
    const [savingPrefs, setSavingPrefs] = useState(false);

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [changingPassword, setChangingPassword] = useState(false);

    const isPasswordAccount = auth.currentUser?.providerData?.some(
        (provider) => provider.providerId === "password"
    );

    useEffect(() => {
        if (!userId) return;
        setPreferences(getUserPreferences(userId));
    }, [userId]);

    const handlePreferenceChange = (field, value) => {
        setPreferences((prev) => ({ ...prev, [field]: value }));
    };

    const handleSavePreferences = (e) => {
        e.preventDefault();
        if (!userId) return;

        setSavingPrefs(true);
        try {
            saveUserPreferences(userId, preferences);
            toast.success("Preferences saved");
        } catch {
            toast.error("Could not save preferences");
        } finally {
            setSavingPrefs(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (!isPasswordAccount) {
            toast.error("Password change is only available for email accounts");
            return;
        }

        if (newPassword.length < 6) {
            toast.error("New password must be at least 6 characters");
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error("New passwords do not match");
            return;
        }

        const firebaseUser = auth.currentUser;
        if (!firebaseUser?.email) {
            toast.error("Unable to verify your account");
            return;
        }

        setChangingPassword(true);

        try {
            const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
            await reauthenticateWithCredential(firebaseUser, credential);
            await updatePassword(firebaseUser, newPassword);

            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            toast.success("Password updated");
        } catch (err) {
            if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
                toast.error("Current password is incorrect");
            } else if (err.code === "auth/weak-password") {
                toast.error("Password is too weak");
            } else {
                toast.error("Could not update password. Please try again.");
            }
        } finally {
            setChangingPassword(false);
        }
    };

    const handleLogout = () => {
        requestLogout();
    };

    return (
        <PageShell>
            <PageHeader
                title="Settings"
                description="Manage your post defaults and account security."
            />

            <form
                onSubmit={handleSavePreferences}
                className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6"
            >
                <div className="mb-5 flex items-center gap-2">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                        <i className="bi bi-sliders" aria-hidden />
                    </span>
                    <div>
                        <h5 className="mb-0 text-lg font-semibold text-slate-900">Post preferences</h5>
                        <p className="mb-0 text-sm text-slate-600">
                            Used as defaults for quick posts and new campaigns.
                        </p>
                    </div>
                </div>

                <div className="form-section mb-5">
                    <p className="form-label mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-800 md:text-sm">
                        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-700">
                            <i className="bi bi-globe2 text-sm" aria-hidden />
                        </span>
                        Default platform
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:gap-3" role="radiogroup">
                        {PLATFORM_OPTIONS.map((p) => {
                            const checked = preferences.platform === p.value;
                            return (
                                <label
                                    key={p.value}
                                    className={`relative flex cursor-pointer flex-col items-center gap-2 rounded-xl border px-2 py-3 text-center transition-all duration-200 md:px-3 md:py-4 ${
                                        checked
                                            ? "border-slate-900 bg-slate-50 ring-2 ring-slate-900/20"
                                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="platform"
                                        value={p.value}
                                        checked={checked}
                                        onChange={() => handlePreferenceChange("platform", p.value)}
                                        className="sr-only"
                                    />
                                    <span
                                        className={`flex h-10 w-10 items-center justify-center rounded-full bg-white/90 md:h-12 md:w-12 ${checked ? "ring-1 ring-slate-300" : ""}`}
                                    >
                                        <i className={`${p.icon} text-2xl md:text-[1.65rem] ${p.iconClass}`} aria-hidden />
                                    </span>
                                    <span className="text-[11px] font-semibold text-slate-800 md:text-xs">{p.label}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>

                <div className="form-section mb-5">
                    <p className="form-label mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-800 md:text-sm">
                        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-700">
                            <i className="bi bi-translate text-sm" aria-hidden />
                        </span>
                        Default language
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 md:gap-3" role="radiogroup">
                        {LANGUAGE_OPTIONS.map((lang) => {
                            const checked = preferences.language === lang.value;
                            return (
                                <label
                                    key={lang.value}
                                    className={`relative flex cursor-pointer flex-col items-center gap-2 rounded-xl border px-2 py-3 text-center transition-all duration-200 md:px-3 md:py-4 ${
                                        checked
                                            ? "border-slate-900 bg-slate-50 ring-2 ring-slate-900/20"
                                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="language"
                                        value={lang.value}
                                        checked={checked}
                                        onChange={() => handlePreferenceChange("language", lang.value)}
                                        className="sr-only"
                                    />
                                    <span
                                        className={`inline-flex min-h-10 min-w-10 items-center justify-center rounded-full px-2.5 text-sm font-bold text-slate-800 md:min-h-12 md:min-w-12 md:text-base ${
                                            checked ? "bg-blue-100 ring-1 ring-blue-200" : "bg-blue-50"
                                        }`}
                                    >
                                        {lang.badge}
                                    </span>
                                    <span className="text-[11px] font-semibold text-slate-800 md:text-xs">{lang.label}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>

                <Input
                    label="Default target audience"
                    labelIcon="bullseye"
                    type="text"
                    placeholder="e.g., Students, travelers, startup founders"
                    value={preferences.target}
                    onChange={(e) => handlePreferenceChange("target", e.target.value)}
                    wrapperClassName="mb-5"
                />

                <button
                    type="submit"
                    className="btn btn-dark-app fw-semibold disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={savingPrefs}
                >
                    {savingPrefs ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                            Saving…
                        </>
                    ) : (
                        <>
                            <i className="bi bi-check2-circle me-2" aria-hidden />
                            Save preferences
                        </>
                    )}
                </button>
            </form>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                <div className="mb-5 flex items-center gap-2">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                        <i className="bi bi-shield-lock" aria-hidden />
                    </span>
                    <div>
                        <h5 className="mb-0 text-lg font-semibold text-slate-900">Change password</h5>
                        <p className="mb-0 text-sm text-slate-600">Update your login credentials.</p>
                    </div>
                </div>

                {isPasswordAccount ? (
                    <form onSubmit={handleChangePassword} className="max-w-md">
                        <Input
                            label="Current password"
                            labelIcon="lock"
                            type="password"
                            autoComplete="current-password"
                            placeholder="Enter current password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            wrapperClassName="mb-4"
                        />
                        <Input
                            label="New password"
                            labelIcon="key"
                            type="password"
                            autoComplete="new-password"
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            wrapperClassName="mb-4"
                        />
                        <Input
                            label="Confirm new password"
                            labelIcon="key-fill"
                            type="password"
                            autoComplete="new-password"
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            wrapperClassName="mb-5"
                        />
                        <button
                            type="submit"
                            className="btn btn-dark-outline-app fw-semibold disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={changingPassword}
                        >
                            {changingPassword ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                                    Updating…
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-shield-check me-2" aria-hidden />
                                    Update password
                                </>
                            )}
                        </button>
                    </form>
                ) : (
                    <p className="mb-0 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                        You signed in with Google. Password changes are managed through your Google account.
                    </p>
                )}
            </div>

            <div className="mt-6 flex justify-end">
                <button type="button" className="btn btn-secondary-app" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-2" aria-hidden />
                    Log out
                </button>
            </div>
        </PageShell>
    );
};

export default Settings;
